import { useRef, useState } from 'preact/hooks';
import dayjs from 'dayjs';
import { rpx } from 'client/lib/rpx-client';
import { isNetworkError } from 'client/lib/ajax';
import { BtnPrimary, BtnSecondary, BtnWarning } from '@components/buttons';
import { showToast } from '@components/toaster';
import type { CheckinFeedItem } from 'server/dbx/checkin-feedback';
import {
  CHECKIN_QUICK_REPLIES,
  CHECKIN_REACTIONS,
  CheckinFeedback,
  CheckinFeedbackReply,
  CheckinReactionKey,
  firstAcknowledgedBy,
  isStuckStandup,
  isUpdatedAfterReply,
  CHECKIN_REPLY_EMAIL_DELAY_MINUTES,
  CHECKIN_REPLY_EMAIL_MAX_AGE_HOURS,
  MAX_CHECKIN_REPLY_LENGTH,
  needsReply,
  summaryAsksForNudge,
} from 'shared/checkin-feedback';
import { CheckinItemBody } from './checkin-item-body';
import { checkinItemTitle, submittedLabel } from './checkin-item-labels';

type Props = {
  courseId: UUID;
  item: CheckinFeedItem;
  viewerId: UUID;
  /** Off on a single student's page, where every card is theirs. */
  showName?: boolean;
  studentHref?: string;
  /** The course's standup start date, for summary week numbers. */
  startDate?: string;
  /** The course emails the student when a coach replies. */
  replyEmailsEnabled?: boolean;
  /** Whose student this is, when it isn't the viewer's: "Meredith’s student". */
  coachNote?: string;
  /** The student's tracker counts this week, shown under a standup's answers. */
  trackerLine?: string;
  onFeedback(feedback: CheckinFeedback): void;
};

/**
 * Runs one change to this card at a time. Each response carries the card's
 * whole feedback, so two changes in flight could otherwise land out of
 * order and the older response would wipe out the newer change.
 */
type Run = <T>(fn: () => Promise<T>) => Promise<T>;

function firstName(name: string) {
  return name.trim().split(/\s+/)[0] || name;
}

function Chip({ tone, children }: { tone: 'need' | 'stuck' | 'ack' | 'muted'; children: string }) {
  const tones = {
    need: 'bg-red-50 text-red-700',
    stuck: 'bg-amber-50 text-amber-800',
    ack: 'bg-green-50 text-green-700',
    muted: 'bg-gray-100 text-gray-600',
  };
  return (
    <span class={`inline-block text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${tones[tone]}`}>
      {children}
    </span>
  );
}

/**
 * Turn a failed post into words. A dropped connection never reaches the
 * server; anything else carries the server's own message.
 */
function failureText(err: unknown, action: string) {
  const message = (err as Error)?.message;
  if (!message || isNetworkError(err as Error)) {
    return `Your ${action} didn’t go through because the connection dropped. Your text is still here, so try again.`;
  }
  return `Your ${action} didn’t go through. ${message}`;
}

function Reactions({
  courseId,
  item,
  viewerId,
  run,
  onFeedback,
}: Pick<Props, 'courseId' | 'item' | 'viewerId' | 'onFeedback'> & { run: Run }) {
  const [busy, setBusy] = useState<CheckinReactionKey>();

  async function toggle(reaction: CheckinReactionKey, on: boolean) {
    setBusy(reaction);
    try {
      onFeedback(
        await run(() =>
          rpx.checkins.setCheckinReaction({
            courseId,
            item: { kind: item.kind, id: item.id },
            reaction,
            on,
          }),
        ),
      );
    } catch (err) {
      showReactionError(err);
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <div class="flex flex-wrap gap-2" role="group" aria-label="Reactions">
      {CHECKIN_REACTIONS.map((r) => {
        const who = item.feedback.reactions.filter((x) => x.reaction === r.key);
        const mine = who.some((x) => x.coachUserId === viewerId);
        const names = who.map((x) => (x.coachUserId === viewerId ? 'You' : x.coachName)).join(', ');
        return (
          <button
            key={r.key}
            type="button"
            aria-pressed={mine}
            aria-label={`${mine ? 'Remove' : 'React with'} ${r.label}`}
            disabled={!!busy}
            onClick={() => toggle(r.key, !mine)}
            class={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-sm disabled:opacity-60 ${
              mine
                ? 'bg-indigo-50 border-indigo-500 text-indigo-800'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <span aria-hidden="true">{r.emoji}</span>
            {names && <span class="text-xs">{names}</span>}
          </button>
        );
      })}
    </div>
  );
}

function showReactionError(err: unknown) {
  showToast({
    type: 'warn',
    title: 'Reaction didn’t save',
    message: isNetworkError(err as Error)
      ? 'The connection dropped. Try again.'
      : (err as Error)?.message,
  });
}

function ReplyRow({
  courseId,
  reply,
  viewerId,
  studentFirst,
  emailAt,
  run,
  onFeedback,
}: {
  courseId: UUID;
  reply: CheckinFeedbackReply;
  viewerId: UUID;
  studentFirst: string;
  /** When this not-yet-emailed reply's email goes, if it will. */
  emailAt?: Date;
  run: Run;
  onFeedback(feedback: CheckinFeedback): void;
}) {
  const [mode, setMode] = useState<'view' | 'edit' | 'confirmDelete'>('view');
  const [text, setText] = useState(reply.body);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const isMine = !!reply.authorUserId && reply.authorUserId === viewerId;
  const emailPending = !!emailAt;

  async function save() {
    setIsSaving(true);
    setError('');
    try {
      onFeedback(
        await run(() => rpx.checkins.editCheckinReply({ courseId, replyId: reply.id, body: text })),
      );
      setMode('view');
    } catch (err) {
      setError(failureText(err, 'edit'));
    } finally {
      setIsSaving(false);
    }
  }

  async function remove() {
    setIsSaving(true);
    setError('');
    try {
      onFeedback(await run(() => rpx.checkins.deleteCheckinReply({ courseId, replyId: reply.id })));
    } catch (err) {
      setError(failureText(err, 'delete'));
      setIsSaving(false);
    }
  }

  return (
    <div class="flex flex-col gap-1 border-l-2 border-indigo-200 pl-3">
      <div class="flex flex-wrap items-center gap-2 text-sm">
        <strong class="text-gray-800">{reply.authorName}</strong>
        <span class="text-gray-500">
          Coach · {dayjs(reply.createdAt).format('ddd, MMM D, h:mm A')}
        </span>
        {reply.isEdited && <Chip tone="muted">Edited</Chip>}
        {emailPending && (
          <Chip tone="muted">{`Email goes after ${dayjs(emailAt).format('h:mm A')}`}</Chip>
        )}
      </div>
      {mode === 'edit' ? (
        <div class="flex flex-col gap-2">
          <label class="sr-only" for={`edit-${reply.id}`}>
            Edit your reply
          </label>
          <textarea
            id={`edit-${reply.id}`}
            rows={3}
            class="ruz-input"
            value={text}
            maxLength={MAX_CHECKIN_REPLY_LENGTH}
            onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
          />
          <div class="flex items-center gap-2">
            <BtnPrimary onClick={save} isLoading={isSaving} disabled={!text.trim()}>
              Save
            </BtnPrimary>
            <BtnSecondary
              onClick={() => {
                setText(reply.body);
                setMode('view');
                setError('');
              }}
              disabled={isSaving}
            >
              Cancel
            </BtnSecondary>
            {reply.emailSentAt && (
              <span class="text-sm text-gray-500">
                {studentFirst} already got the first version by email.
              </span>
            )}
          </div>
        </div>
      ) : (
        <p class="whitespace-pre-wrap text-gray-800">{reply.body}</p>
      )}
      {mode === 'confirmDelete' && (
        <div
          role="alert"
          class="flex flex-wrap items-center gap-2 bg-red-50 border border-red-200 rounded-md p-2 text-sm"
        >
          <span class="grow">
            Delete this reply?{' '}
            {reply.emailSentAt
              ? `${studentFirst} already got it by email.`
              : emailPending
                ? `${studentFirst} won’t be emailed.`
                : `${studentFirst} won’t see it any more.`}
          </span>
          <BtnWarning onClick={remove} isLoading={isSaving}>
            Delete
          </BtnWarning>
          <BtnSecondary onClick={() => setMode('view')} disabled={isSaving}>
            Keep
          </BtnSecondary>
        </div>
      )}
      {isMine && mode === 'view' && (
        <div class="flex gap-3 text-sm">
          <button
            type="button"
            class="text-indigo-600 hover:underline"
            onClick={() => setMode('edit')}
          >
            Edit
          </button>
          <button
            type="button"
            class="text-indigo-600 hover:underline"
            onClick={() => setMode('confirmDelete')}
          >
            Delete
          </button>
        </div>
      )}
      {error && (
        <p role="alert" class="text-sm text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}

function Composer({
  courseId,
  item,
  replyEmailsEnabled,
  run,
  onFeedback,
}: Pick<Props, 'courseId' | 'item' | 'replyEmailsEnabled' | 'onFeedback'> & { run: Run }) {
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [error, setError] = useState('');
  const textRef = useRef<HTMLTextAreaElement>(null);
  const first = firstName(item.studentName);

  if (!isOpen) {
    return (
      <div>
        <BtnSecondary onClick={() => setIsOpen(true)}>Reply</BtnSecondary>
      </div>
    );
  }

  function addQuick(quick: string) {
    setText((current) => (current.trim() ? `${current.trimEnd()} ${quick}` : quick));
    textRef.current?.focus();
  }

  async function post() {
    setIsPosting(true);
    setError('');
    try {
      onFeedback(
        await run(() =>
          rpx.checkins.addCheckinReply({
            courseId,
            item: { kind: item.kind, id: item.id },
            body: text,
          }),
        ),
      );
      setText('');
      setIsOpen(false);
    } catch (err) {
      setError(failureText(err, 'reply'));
    } finally {
      setIsPosting(false);
    }
  }

  return (
    <div class="flex flex-col gap-2">
      <label class="text-sm text-gray-600" for={`reply-${item.id}`}>
        Reply to {first} ·{' '}
        {replyEmailsEnabled && item.studentGetsEmail
          ? `${first} sees it in the course and gets it by email`
          : `${first} sees it in the course`}
      </label>
      <textarea
        id={`reply-${item.id}`}
        rows={4}
        ref={textRef}
        class="ruz-input"
        value={text}
        placeholder={`Write a reply to ${first}`}
        maxLength={MAX_CHECKIN_REPLY_LENGTH}
        disabled={isPosting}
        autoFocus
        onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
      />
      <div class="flex flex-wrap gap-2" aria-label="Quick replies">
        {CHECKIN_QUICK_REPLIES.map((q) => (
          <button
            key={q.label}
            type="button"
            title={q.text}
            disabled={isPosting}
            onClick={() => addQuick(q.text)}
            class="px-2.5 py-1 rounded-full border border-gray-300 bg-white text-xs text-gray-700 hover:bg-gray-50"
          >
            {q.label}
          </button>
        ))}
      </div>
      {error && (
        <p role="alert" class="text-sm text-red-700">
          {error}
        </p>
      )}
      <div class="flex items-center gap-2">
        <BtnPrimary onClick={post} isLoading={isPosting} disabled={!text.trim()}>
          Post reply
        </BtnPrimary>
        <BtnSecondary
          onClick={() => {
            setIsOpen(false);
            setError('');
          }}
          disabled={isPosting}
        >
          Cancel
        </BtnSecondary>
      </div>
    </div>
  );
}

export function CoachItemCard({
  courseId,
  item,
  viewerId,
  showName = true,
  studentHref,
  startDate,
  replyEmailsEnabled,
  coachNote,
  trackerLine,
  onFeedback,
}: Props) {
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const run: Run = (fn) => {
    const next = chain.current.then(fn);
    chain.current = next.catch(() => undefined);
    return next;
  };
  // The mailer sends every waiting reply together, once the oldest has
  // waited long enough. A reply it has already taken, sent or not, isn't
  // waiting; nor is one too old to send, or to a student who gets no email.
  const oldest = Date.now() - CHECKIN_REPLY_EMAIL_MAX_AGE_HOURS * 60 * 60 * 1000;
  const waiting =
    item.studentGetsEmail && replyEmailsEnabled
      ? item.feedback.replies.filter(
          (r) => !r.emailSentAt && !r.emailClaimedAt && r.createdAt.getTime() > oldest,
        )
      : [];
  const emailAt = waiting.length
    ? new Date(
        Math.min(...waiting.map((r) => r.createdAt.getTime())) +
          CHECKIN_REPLY_EMAIL_DELAY_MINUTES * 60 * 1000,
      )
    : undefined;
  const stuck = item.kind === 'standup' && isStuckStandup(item.answers);
  const asksNudge = item.kind === 'summary' && summaryAsksForNudge(item.body);
  const needsAReply = needsReply(item);
  const acknowledgedBy = firstAcknowledgedBy(item.feedback);

  return (
    <article class="bg-white border rounded-sm p-4 flex flex-col gap-4">
      <header class="flex flex-wrap items-start justify-between gap-2">
        <div class="flex flex-col">
          {showName &&
            (studentHref ? (
              <a class="font-semibold text-indigo-600" href={studentHref}>
                {item.studentName}
              </a>
            ) : (
              <strong class="text-gray-800">{item.studentName}</strong>
            ))}
          <span class={showName ? 'text-sm text-gray-600' : 'font-semibold text-gray-800'}>
            {checkinItemTitle(item, startDate)}
          </span>
          <span class="text-sm text-gray-500">
            {submittedLabel(item)}
            {coachNote && ` · ${coachNote}`}
          </span>
        </div>
        <div class="flex flex-wrap gap-1.5">
          {stuck && <Chip tone="stuck">Stuck</Chip>}
          {asksNudge && <Chip tone="stuck">Asks for a nudge</Chip>}
          {item.isLate && <Chip tone="muted">Sent late</Chip>}
          {isUpdatedAfterReply(item) && <Chip tone="need">Updated after reply</Chip>}
          {needsAReply ? (
            <Chip tone="need">Needs a reply</Chip>
          ) : (
            <Chip tone="ack">{`✓ Acknowledged by ${acknowledgedBy}`}</Chip>
          )}
          {item.isEdited && !isUpdatedAfterReply(item) && <Chip tone="muted">Edited</Chip>}
        </div>
      </header>
      <CheckinItemBody kind={item.kind} answers={item.answers} body={item.body} highlight />
      {trackerLine && item.kind === 'standup' && (
        <p class="text-sm text-gray-700">This week so far: {trackerLine}</p>
      )}
      <footer class="flex flex-col gap-3 border-t pt-3">
        <Reactions
          courseId={courseId}
          item={item}
          viewerId={viewerId}
          run={run}
          onFeedback={onFeedback}
        />
        {item.feedback.replies.map((reply) => (
          <ReplyRow
            key={reply.id}
            courseId={courseId}
            reply={reply}
            viewerId={viewerId}
            studentFirst={firstName(item.studentName)}
            emailAt={waiting.includes(reply) ? emailAt : undefined}
            run={run}
            onFeedback={onFeedback}
          />
        ))}
        <Composer
          courseId={courseId}
          item={item}
          replyEmailsEnabled={replyEmailsEnabled}
          run={run}
          onFeedback={onFeedback}
        />
      </footer>
    </article>
  );
}
